import posthog from 'posthog-js';

/**
 * Analytics wrapper. Call analytics.init() once at app start, analytics.identify()
 * on login, and analytics.track() anywhere you want to record a user action.
 *
 * Event taxonomy (keep names stable — don't rename, add new events instead):
 *   // Auth
 *   signup_completed             { method: 'email'|'google'|'apple', source: str }   [server-side]
 *   login_success                { method: 'email'|'google'|'apple' }
 *   login_failed                 { reason: string }
 *
 *   // Onboarding
 *   onboarding_step_completed    { step_number: int }
 *   onboarding_complete          { final_step: int }
 *
 *   // Social connections
 *   social_connection_completed  { provider: 'gmail'|'x'|'youtube' }
 *   social_connection_failed     { provider, error: str }
 *
 *   // Family
 *   family_created               { max_members: int }                                 [server-side]
 *   family_invite_sent           { invited_user_type: 'athlete'|'guardian' }           [server-side]
 *   family_invite_accepted       { via: 'email'|'link' }                               [server-side]
 *
 *   // Team
 *   team_code_redeemed           { team_id, via: 'team_code', success: bool }         [server-side]
 *   admin_team_assigned          { team_id, athlete_profile_id }                       [server-side]
 *
 *   // Coach comms
 *   coach_message_logged         { kind: 'email'|'dm'|'tweet', target: 'athlete'|'school' } [server-side]
 *
 *   // Subscription (all server-side, from Stripe webhooks)
 *   subscription_started         { plan, amount_cents, interval, via: 'stripe', trigger_action?: str }
 *   subscription_renewed         { plan, amount_cents }
 *   subscription_cancelled       { plan, at_period_end: bool }
 *   invoice_paid                 { amount_cents, currency }
 *
 *   // Paywall funnel (freemium-redesign Phase 2 P2.3 — used to measure
 *   // which gates convert and which dismiss). Naming matches the spec
 *   // in /web/ONBOARDING_FREEMIUM_REDESIGN_PLAN.md §P2.3.
 *   paywall_shown                { trigger_action: str }
 *   paywall_dismissed            { trigger_action: str, time_open_ms: int }
 *   paywall_converted            { trigger_action: str, plan, amount_cents, via: 'stripe'|'rc' }  [server-side for stripe; client for mobile RC]
 *
 *   // JIT data-entry prompts (freemium-redesign Phase 2 P2.1) — measure
 *   // completion rate per prompt surface so we can tune which prompts
 *   // earn their keep.
 *   jit_prompt_shown             { surface: str, missing_fields: str[] }
 *   jit_prompt_completed         { surface: str, fields_filled: str[] }
 *   jit_prompt_dismissed         { surface: str, missing_fields: str[] }
 *
 *   // Admin
 *   admin_user_deleted           { target_user_id, soft: bool }                        [server-side]
 *   admin_user_reactivated       { target_user_id }                                    [server-side]
 *   admin_created                { email }                                             [server-side]
 *   admin_role_assigned          { target_user_id, role_name }                         [server-side]
 *   admin_role_revoked           { target_user_id, role_name }                         [server-side]
 *
 *   // Mobile-only
 *   push_notification_opened     { notification_type: str, deep_link: str|null }       [mobile]
 *
 * Privacy rules:
 *   - distinct_id is always users.id (UUID). Never email, never Auth0 sub.
 *   - Never put email/name/DOB/PII in event properties. Use identify(userId, traits)
 *     to set them as Person properties once; PostHog looks them up from the distinct_id.
 *   - Session recording masks all inputs by default.
 */

const POSTHOG_KEY = 'phc_x6DVxkGKE2mPXfVYLhCN67pvzBU9kKAJX2JeATyvMMsd';
const POSTHOG_HOST = 'https://us.i.posthog.com';

let initialized = false;

export const analytics = {
  init(): void {
    if (initialized || typeof window === 'undefined') return;
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      person_profiles: 'identified_only',
      // Share the anonymous distinct_id with apsrecruits.com (the marketing site)
      // so a visitor's pre-signup session merges into their Person on signup.
      cross_subdomain_cookie: true,
      autocapture: true,
      capture_pageview: true,
      capture_pageleave: true,
      session_recording: {
        maskAllInputs: true,
      },
      loaded: () => {
        initialized = true;
      },
    });
    initialized = true;
  },

  /**
   * Tie all subsequent events to this user. Traits are stored as Person properties
   * (email, name, user_type, family_id, created_at) so they're queryable in PostHog
   * without ever being embedded in event payloads.
   */
  identify(userId: string, traits: Record<string, any> = {}): void {
    if (!initialized) return;
    posthog.identify(userId, traits);
  },

  /**
   * Attach extra properties to the current Person without creating a new identity.
   */
  setPersonProperties(traits: Record<string, any>): void {
    if (!initialized) return;
    posthog.setPersonProperties(traits);
  },

  track(event: string, properties?: Record<string, any>): void {
    if (!initialized) return;
    posthog.capture(event, properties);
  },

  /**
   * Clear the identified user on logout. After this, events become anonymous again.
   */
  reset(): void {
    if (!initialized) return;
    posthog.reset();
  },
};

export default analytics;
